import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useOccupations } from '@/hooks/useOccupations';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';

/**
 * Combobox of canonical mine occupations (= job titles, e.g.
 * "Haul Truck Operator"), sourced from Databricks
 * `employee_roster.job_title` via `useOccupations`.
 *
 * Coordinators can either scroll the dropdown OR type to filter, and
 * free text is preserved — the caller decides whether to accept a
 * non-canonical value at save time. This matches the Add User workflow
 * where a coordinator may add a job title that isn't in the Databricks
 * roster yet (e.g. a new role being introduced on-site).
 *
 * Implementation: a custom floating listbox rather than a native
 * `<input list>` + `<datalist>`. The native datalist popup renders
 * detached and mispositioned inside the VS Code webview / Electron
 * Chromium host — it appeared as a full-height dark panel pinned to the
 * left of the viewport — and native datalists can't be styled or
 * repositioned. This mirrors EmailSuggestInput in the same modal:
 * `position: fixed` anchored to the input's bounding rect, so the list
 * floats correctly above the drawer's `overflow: auto` body.
 *
 * A non-canonical current value ("legacy" — pre-cutover free text or an
 * old orientation-display-name not yet migrated) is preserved in the
 * input and flagged with a hint so the coordinator knows it'll be saved
 * as typed.
 *
 * Error / empty / loading states surface beneath the input so the
 * coordinator can tell what's going on (Databricks down, env not set,
 * etc.) instead of seeing a silent empty dropdown.
 */

/** Cap the floating list height; it scrolls past this. */
const MAX_LIST_HEIGHT = 260;

export function OccupationSelect({
  value,
  onChange,
  label = 'Occupation',
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}) {
  const { occupations, isReady, error } = useOccupations();
  const titles = useMemo(() => occupations.map((o) => o.title), [occupations]);
  const isLegacy = value !== '' && !titles.includes(value);

  const wrapperRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  // Stable id for the aria-controls / listbox binding.
  const listId = useId();

  // Filter on every keystroke. Empty query — or one that exactly matches
  // a canonical title (i.e. right after a pick) — shows the full list so
  // the coordinator can keep browsing; otherwise substring match with
  // prefix-matches ranked first.
  const options = useMemo<string[]>(() => {
    const q = value.trim().toLowerCase();
    const exact = titles.some((t) => t.toLowerCase() === q);
    if (!q || exact) return titles;
    return titles
      .map((t) => {
        const lc = t.toLowerCase();
        const score = lc.startsWith(q) ? 0 : lc.includes(q) ? 1 : -1;
        return { t, score };
      })
      .filter((r) => r.score >= 0)
      .sort((a, b) => a.score - b.score || a.t.localeCompare(b.t))
      .map((r) => r.t);
  }, [titles, value]);

  // Highlight the first row whenever the visible list changes shape.
  useEffect(() => {
    setActiveIdx(options.length > 0 ? 0 : -1);
  }, [options]);

  // Keep the highlighted row visible as arrow keys walk a long, scrolled list.
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  // Measure the input on open + on layout shifts (drawer scroll, window
  // resize) so the fixed-position list tracks it. useLayoutEffect so it
  // positions before paint on the first open frame.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () =>
      setAnchorRect(inputRef.current?.getBoundingClientRect() ?? null);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true); // capture so drawer-body scroll fires it
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, options.length]);

  // Outside-click closes. The list is a DOM descendant of the wrapper
  // label even though it's position:fixed, so `contains` covers clicks on
  // rows too.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (target && wrapperRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const showList = open && titles.length > 0;

  function select(title: string) {
    onChange(title);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (options.length) setActiveIdx((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (options.length)
        setActiveIdx((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && options[activeIdx]) {
        e.preventDefault();
        select(options[activeIdx]);
      }
      // Else: Enter falls through to the surrounding form's default submit.
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <label
      ref={wrapperRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 12,
      }}
    >
      <span className="label-up">{label}</span>
      <input
        ref={inputRef}
        className="inp"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Pick from list or type a job title…"
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
      />
      {showList && anchorRect ? (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          style={{
            position: 'fixed',
            top: anchorRect.bottom + 4,
            left: anchorRect.left,
            width: anchorRect.width,
            maxHeight: MAX_LIST_HEIGHT,
            overflowY: 'auto',
            // `--panel` matches the drawer's own surface so the dropdown
            // reads as a natural extension of the input in both themes.
            background: 'var(--panel)',
            color: 'var(--text)',
            border: '1px solid var(--border-2)',
            borderRadius: 6,
            padding: 4,
            zIndex: 50,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {options.length === 0 ? (
            <div
              style={{ padding: '8px 10px', color: 'var(--muted)', fontSize: 12 }}
            >
              No matching job titles.
            </div>
          ) : (
            options.map((t, i) => (
              <OptionRow
                key={t}
                idx={i}
                title={t}
                active={i === activeIdx}
                onSelect={() => select(t)}
                onHover={() => setActiveIdx(i)}
              />
            ))
          )}
        </div>
      ) : null}
      {error ? (
        <div
          title={error}
          style={{
            fontSize: 10,
            color: 'var(--bad)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
          }}
        >
          <span style={{ display: 'inline-flex', verticalAlign: '-1px', marginRight: 3 }}><Icon of={Icons.status.warning} size={10} /></span>Catalog unavailable, hover for details
        </div>
      ) : !isReady ? (
        <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.3 }}>
          Loading catalog…
        </div>
      ) : titles.length === 0 ? (
        <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.3 }}>
          Catalog empty — you can still type a title.
        </div>
      ) : isLegacy ? (
        <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.3 }}>
          Not in catalog — will be saved as typed.
        </div>
      ) : null}
    </label>
  );
}

function OptionRow({
  idx,
  title,
  active,
  onSelect,
  onHover,
}: {
  idx: number;
  title: string;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={active}
      data-idx={idx}
      // onMouseDown, not onClick — fires before the input's onBlur so the
      // click always lands even while focus is stealing attention.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      style={{
        padding: '6px 8px',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 12.5,
        color: 'var(--text)',
        background: active ? 'var(--panel-2)' : 'transparent',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </div>
  );
}
